import React from 'react';
import { Button } from './ui/button';
import { User } from '../types/user';
import { Badge } from './ui/badge';
import { useState, useRef } from 'react';
import UsageIndicator, { UsageIndicatorRef } from './UsageIndicator';
import PlansModal from './PlansModal';
import { User as UserIcon, CreditCard, LogOut, Settings, Users, Menu } from 'lucide-react';

interface TopHeaderProps {
  user: User;
  tenant?: any;
  onLogout: () => void;
  onCompanySettings?: () => void;
  onUserManagement?: () => void;
  onProfileClick?: () => void;
  refreshTrigger?: number; // Trigger para forçar refresh
  onMenuToggle?: () => void; // Toggle do menu lateral
  showMenuButton?: boolean; // Mostrar botão de menu
}

export function TopHeader({ user, tenant, onLogout, onCompanySettings, onUserManagement, onProfileClick, refreshTrigger, onMenuToggle, showMenuButton }: TopHeaderProps) {
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const usageIndicatorRef = useRef<UsageIndicatorRef>(null);

  // Debug: Verificar dados do tenant
  React.useEffect(() => {
    console.log('🔍 TopHeader - Dados do tenant:', {
      tenantId: tenant?.id,
      accountType: tenant?.accountType,
      userRole: user.role,
      onCompanySettings: !!onCompanySettings
    });
  }, [tenant, user.role, onCompanySettings]);

  // Atualizar contador quando refreshTrigger mudar
  React.useEffect(() => {
    if (refreshTrigger && usageIndicatorRef.current) {
      // Trigger de refresh
      usageIndicatorRef.current.refreshUsage();
    }
  }, [refreshTrigger]);

  return (
    <div className="bg-black text-white border-b px-3 py-1 min-h-[36px]">
      <div className="flex items-center justify-between">
        {/* Logo e título */}
        <div className="flex items-center gap-2">
          {/* Botão de menu para telas menores */}
          {showMenuButton && onMenuToggle && (
            <button
              onClick={onMenuToggle}
              className="lg:hidden p-1.5 hover:bg-white/10 rounded-md transition-colors mr-2"
            >
              <Menu className="w-5 h-5" />
            </button>
          )}
          <a href="/">
            <img src="/space-logo.png" alt="Logo Space" style={{ maxHeight: 24 }} />
          </a>
        </div>
        {/* Centro - Indicador de Uso */}
        {tenant && (
          <div className="flex-1 flex justify-center">
            <UsageIndicator 
              ref={usageIndicatorRef}
              tenantId={tenant.id} 
              userId={user.id}
              className="text-white"
              onUpgradeClick={() => setPlansModalOpen(true)}
            />
          </div>
        )}

        {/* Área do usuário */}
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[10px] px-1 py-0.5">
            {user.role === 'manager' ? 'GESTOR' : 'USUÁRIO'}
          </Badge>
          
          {/* Menu do Usuário com Dropdown */}
          <UserMenu 
            user={user}
            tenant={tenant}
            onProfileClick={onProfileClick}
            onPaymentClick={() => setPlansModalOpen(true)}
            onCompanySettings={onCompanySettings}
            onUserManagement={onUserManagement}
            onLogout={onLogout}
          />
        </div>
      </div>
      
      {/* Modal de Planos */}
      {tenant && (
        <PlansModal
          isOpen={plansModalOpen}
          onClose={() => setPlansModalOpen(false)}
          tenantId={tenant.id}
          currentPlan={tenant.plan?.toLowerCase() || 'free'}
        />
      )}
      
    </div>
  );
}

// Menu do Usuário com dropdown
function UserMenu({ 
  user, 
  tenant,
  onProfileClick, 
  onPaymentClick, 
  onCompanySettings,
  onUserManagement,
  onLogout 
}: { 
  user: User;
  tenant?: any;
  onProfileClick?: () => void;
  onPaymentClick: () => void;
  onCompanySettings?: () => void;
  onUserManagement?: () => void;
  onLogout: () => void;
}) {
  const [open, setOpen] = useState(false);
  
  return (
    <div className="relative">
      <button
        className="text-xs font-medium bg-transparent border-none outline-none px-2 py-1 rounded hover:bg-white/10 flex items-center gap-1"
        onClick={() => setOpen((v) => !v)}
      >
        {user.name}
        <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
          <path d="M5 8l5 5 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      
      {open && (
        <>
          {/* Backdrop para fechar o menu ao clicar fora */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setOpen(false)}
          />
          
          <div className="absolute right-0 mt-1 w-56 bg-white text-black rounded-lg shadow-lg z-50 border border-gray-200 py-1">
            {/* Perfil do Usuário */}
            <button
              className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 text-sm transition-colors"
              onClick={() => { 
                setOpen(false); 
                if (onProfileClick) onProfileClick(); 
              }}
            >
              <UserIcon className="w-4 h-4 text-gray-600" />
              <span>Perfil</span>
            </button>
            
            {/* Pagamento e Planos - REMOVIDO */}
            {/* {!(user.role === 'user' && (tenant?.accountType === 'business' || tenant?.accountType === 'enterprise')) && (
              <button
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 text-sm transition-colors"
                onClick={() => { 
                  setOpen(false); 
                  onPaymentClick(); 
                }}
              >
                <CreditCard className="w-4 h-4 text-gray-600" />
                <span>Pagamento</span>
              </button>
            )} */}
            
            {/* Gestão de Usuários - OCULTO (acesso apenas por URL: /user-management) */}
            {/* {(user.role === 'owner' || user.role === 'manager') && onUserManagement && (
              <button
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 text-sm transition-colors border-t border-gray-100"
                onClick={() => { 
                  setOpen(false); 
                  onUserManagement(); 
                }}
              >
                <Users className="w-4 h-4 text-gray-600" />
                <span>Gestão de Usuários</span>
              </button>
            )} */}

            {/* Configurações da Empresa (só para contas business e enterprise) */}
            {(user.role === 'owner' || user.role === 'manager') && onCompanySettings && (tenant?.accountType === 'business' || tenant?.accountType === 'enterprise') && (
              <button
                className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center gap-3 text-sm transition-colors"
                onClick={() => { 
                  setOpen(false); 
                  onCompanySettings(); 
                }}
              >
                <Settings className="w-4 h-4 text-gray-600" />
                <span>Configurações da Empresa</span>
              </button>
            )}
            
            {/* Sair */}
            <button
              className="w-full text-left px-4 py-2.5 hover:bg-red-50 flex items-center gap-3 text-sm transition-colors border-t border-gray-100 text-red-600"
              onClick={() => { 
                setOpen(false); 
                onLogout(); 
              }}
            >
              <LogOut className="w-4 h-4" />
              <span>Sair</span>
            </button>
          </div>
        </>
      )}
    </div>
  );
}